import { Injectable, signal } from "@angular/core";
import { BehaviorSubject } from "rxjs";

export interface LandingAppSettings {
  language: string;
  theme: "light" | "dark";
}

const STORAGE_KEY = "Treeslo_Settings";

const ACTIVE_THEME_CLASS = "green_theme";

const defaults: LandingAppSettings = {
  language: "en",
  theme: "light",
};

@Injectable({ providedIn: "root" })
export class LandingSettingsService {
  private readonly optionsSignal = signal<LandingAppSettings>(this.readStored());
  private readonly notify$ = new BehaviorSubject<LandingAppSettings>(
    this.optionsSignal(),
  );

  get notify() {
    return this.notify$.asObservable();
  }

  getOptions(): LandingAppSettings {
    return this.optionsSignal();
  }

  getLanguage(): string {
    return this.getOptions().language;
  }

  setOptions(options: Partial<LandingAppSettings>): void {
    this.optionsSignal.update((current) => ({ ...current, ...options }));
    this.persist();
    this.applyThemeToDocument();
    this.notify$.next(this.optionsSignal());
  }

  setLanguage(lang: string): void {
    this.setOptions({ language: lang });
  }

  applyThemeToDocument(): void {
    if (typeof document === "undefined") {
      return;
    }

    const root = document.documentElement;
    const theme = this.getOptions().theme;

    root.classList.forEach((className) => {
      if (className.endsWith("_theme") && className !== ACTIVE_THEME_CLASS) {
        root.classList.remove(className);
      }
    });
    root.classList.add(ACTIVE_THEME_CLASS);

    if (theme === "dark") {
      root.classList.add("dark-theme");
      root.classList.remove("light-theme");
    } else {
      root.classList.remove("dark-theme");
      root.classList.add("light-theme");
    }
  }

  private readStored(): LandingAppSettings {
    if (typeof localStorage === "undefined") {
      return defaults;
    }

    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        return defaults;
      }

      const parsed = JSON.parse(raw) as Partial<LandingAppSettings>;
      return {
        language: parsed.language ?? defaults.language,
        theme: parsed.theme === "dark" ? "dark" : "light",
      };
    } catch {
      return defaults;
    }
  }

  private persist(): void {
    if (typeof localStorage === "undefined") {
      return;
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(this.optionsSignal()));
  }
}
